import React, { useState, useEffect, useCallback } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import PageTitle from '../../components/PageTitle';

interface Package {
  id: string;
  'package-name': string;
  type: string;
  'roof-size': string;
  'annual-generation': string;
  'solar-panel': string;
  inverter: string;
  'ac-cable': string;
  cable: string;
  connectors: string;
  dcdb: string;
  acdb: string;
  indicator: string;
  kit: string;
  meter: string;
  'meter-box': string;
  structure: string;
  earthing: string;
  'earthing-wire': string;
  wire: string;
}

const Packages: React.FC = () => {
  // Add shimmer animation keyframes using style tag
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% { transform: translateX(-200%); }
        100% { transform: translateX(200%); }
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('Residential');

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      // Get packages collection reference
      const packagesRef = collection(db, 'packages');
      
      // Create a query to fetch all packages of the selected type
      const q = query(packagesRef, where('type', '==', selectedType));
      
      // Execute the query
      const querySnapshot = await getDocs(q);
      
      // Map the documents to package data with all required fields
      let packagesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Package[];
      
      // Sort packages by extracting KW from package-name
      packagesData.sort((a, b) => {
        // Extract numeric value from package-name (e.g., "25 KW" -> 25)
        const getKW = (name: string) => {
          if (!name) return 0;
          // Match numbers followed by optional space and KW (case insensitive)
          const match = name.match(/(\d+)/);
          return match ? parseInt(match[1], 10) : 0;
        };
        
        const aKW = getKW(a['package-name']);
        const bKW = getKW(b['package-name']);
        
        // Sort by the extracted KW value
        return aKW - bKW;
      });
      
      // Log the sorted package names for debugging
      console.log(`Sorted ${selectedType} packages by KW:`);
      packagesData.forEach(pkg => {
        console.log(`- ${pkg['package-name']}`);
      });
      
      setPackages(packagesData);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedType]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleTypeChange = (type: string) => {
    setSelectedType(type);
  };

  const TypeFilterButton: React.FC<{ type: string; label: string; selected: boolean }> = ({ type, label, selected }) => (
    <button
      onClick={() => handleTypeChange(type)}
      className={`relative px-8 py-4 text-lg font-semibold transition-all duration-500 ${selected ? 'text-white' : 'text-blue-200 hover:text-white'}`}
    >
      {label}
      <div
        className={`absolute bottom-0 left-0 w-full h-1 transform transition-all duration-500 ${selected ? 'bg-blue-500 scale-x-100' : 'bg-transparent scale-x-0'}`}
      />
      <div
        className={`absolute inset-0 bg-blue-500/10 transform transition-all duration-500 rounded-lg ${selected ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
      />
    </button>
  );

  return (
    <div className="bg-gray-900">
      <PageTitle
        title="Power Your Home with the Sun"
        subtitle="Maximize Savings & Minimize Bills with SS Solar Rooftop Solutions"
      />
      
      <div className="relative min-h-fit py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-800 to-black">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_50%_-100%,#3b82f6,transparent)] opacity-20" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_80%_60%,#6366f1,transparent)] opacity-20" />
          </div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Type Filter Buttons */}
          <div className="flex justify-center space-x-8 mb-12">
            <TypeFilterButton type="Residential" label="Residential" selected={selectedType === 'Residential'} />
            <TypeFilterButton type="Commercial" label="Commercial" selected={selectedType === 'Commercial'} />
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              <p className="mt-4 text-blue-200">Loading packages...</p>
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-blue-200">No packages found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className="group relative overflow-hidden rounded-xl border border-white/10 hover:border-white/20 transition-all duration-700 p-6 hover:shadow-[0_0_30px_rgba(59,130,246,0.3)] before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/5 before:to-transparent before:translate-x-[-200%] before:animate-[shimmer_3s_infinite] before:hover:animate-[shimmer_2s_infinite] after:absolute after:inset-0 after:bg-[radial-gradient(600px_circle_at_var(--mouse-x,0)_var(--mouse-y,0),rgba(59,130,246,0.1),transparent_40%)] after:opacity-0 after:transition-opacity after:duration-500 hover:after:opacity-100"
                  style={{
                    background: 'linear-gradient(45deg, rgba(59,130,246,0.03) 0%, rgba(255,255,255,0.05) 50%, rgba(59,130,246,0.03) 100%)'
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = ((e.clientX - rect.left) * 100) / rect.width;
                    const y = ((e.clientY - rect.top) * 100) / rect.height;
                    e.currentTarget.style.setProperty('--mouse-x', `${x}%`);
                    e.currentTarget.style.setProperty('--mouse-y', `${y}%`);
                  }}
                >
                  <div className="space-y-4">
                    <h3 className="text-2xl font-bold text-white">{pkg['package-name']}</h3>
                    
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-200 text-sm font-medium">
                      <span className="mr-1">Roof Size:</span>
                      <span>{pkg['roof-size']}</span>
                    </div>

                    <div className="space-y-3 text-blue-100">
                          {[[
                            'Solar Panel',
                            pkg['solar-panel']
                          ],
                          ['Inverter', pkg.inverter],
                          ['Structure', pkg.structure],
                          ['Earthing', pkg.earthing],
                          ['Indicator', pkg.indicator],
                          ['DCDB', pkg.dcdb],
                          ['Wire', pkg.wire],
                          ['Cable', pkg.cable],
                          ['Earthing Wire', pkg['earthing-wire']],
                          ['AC Cable', pkg['ac-cable']],
                          ['Meter', pkg.meter],
                          ['Meter Box', pkg['meter-box']],
                          ['Connectors', pkg.connectors],
                          ['Kit', pkg.kit],].map(([label, value], index) => (
                            <div key={index} className="flex items-center space-x-2 group">
                              <div className="w-3 h-3 bg-blue-400 rounded-full transform transition-transform duration-300 group-hover:scale-125" />
                              <span className="text-sm font-medium group-hover:text-white transition-colors duration-300">
                                {label}: {value}
                              </span>
                            </div>
                          ))}
                        </div>

                    <div className="mt-6 pt-4 border-t border-white/10">
                      <div className="flex items-center justify-between text-white">
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                            <span className="text-xs text-blue-100">⚡</span>
                          </div>
                          <span className="text-sm font-medium">Annual Generation</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl font-bold">{pkg['annual-generation']}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <a
                        href="/inquiry"
                        className="inline-flex items-center gap-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-sm font-medium text-white hover:from-blue-400 hover:to-indigo-400 transition-all duration-300 group-hover:opacity-90"
                      >
                        Get Started
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Packages;